import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import client from "../lib/api";
import {
  Truck, ArrowRight, CheckCircle, Sparkle, Clock, CurrencyDollar,
  Star, ChatCircleDots, ReceiptX, Package, Circuitry, ShieldCheck, X,
  PaperPlaneRight, Copy
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { trackConversion } from "../lib/analytics";

/*
  Owner-Op landing page — dedicated for Google Ads traffic.
  Focused on 1-3 truck buyers only. No enterprise noise.
  Points UTM params to /signup and fires Meta Pixel conversion events.
*/

const PAIN_POINTS = [
  { icon: Clock, title: "You are the dispatcher.", body: "Every morning you text 3 drivers, update customers, chase invoices. Two hours gone before your first cup of coffee." },
  { icon: ReceiptX, title: "You are the accountant.", body: "Invoices sit unpaid because you didn't have time to follow up. Factoring companies eat your margins." },
  { icon: Package, title: "You are the driver.", body: "You're behind the wheel 5 days a week. There's nobody to run the office while you're gone." },
];

const FEATURES = [
  { icon: ChatCircleDots, title: "AI drafts every message", body: "Dispatch texts, customer updates, driver check-ins, invoice reminders — one click, done in 3 seconds." },
  { icon: CurrencyDollar, title: "AI profit calculator", body: "Type any load offer, see if it's a keeper. Fuel spread, deadhead, driver pay all baked in." },
  { icon: Package, title: "Public tracking for your customers", body: "Send one link. They see live status, POD photos, ETA. No more 'where's my load?' calls." },
  { icon: ShieldCheck, title: "Driver Portal on their phone", body: "Drivers tap 'Arrived at Pickup' → dispatch knows instantly. No apps to install." },
];

const TIERS = [
  {
    name: "Solo",
    price: 49,
    trucks: "1 truck",
    tagline: "You + the road",
    features: ["Unlimited AI drafts", "Public shipment tracking", "Driver Portal", "Email support"],
    cta: "Start Solo",
  },
  {
    name: "Duo",
    price: 89,
    trucks: "2 trucks",
    tagline: "You + a partner",
    features: ["Everything in Solo", "2 driver profiles", "AI driver assignment", "Priority AI queue"],
    cta: "Start Duo",
    popular: true,
  },
  {
    name: "Trio",
    price: 129,
    trucks: "3 trucks",
    tagline: "Growing small fleet",
    features: ["Everything in Duo", "3 driver profiles", "Recruiting pipeline", "Live chat support"],
    cta: "Start Trio",
  },
];

export default function OwnerOpLandingPage() {
  const [scenario, setScenario] = useState("Load LD-1002 from Chicago, IL to Denver, CO. Driver: James Carter, Truck T-101. Pickup tomorrow 8am.");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  // Fire "landing page viewed" pixel event on mount
  useEffect(() => {
    trackConversion("landing_view", { variant: "owner_ops" });
  }, []);

  const generate = async () => {
    if (loading || !scenario.trim()) return;
    setLoading(true);
    setOutput("");
    try {
      const { data } = await client.post("/public/ai-draft", {
        category: "dispatch_notification",
        situation: scenario,
      });
      setOutput(data.text || "");
      trackConversion("ai_playground_generate", { variant: "owner_ops" });
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI is busy — try again in a moment.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const copyOutput = () => {
    if (!output) return;
    navigator.clipboard.writeText(output);
    toast.success("Copied");
  };

  return (
    <div className="min-h-screen relative noise-bg" data-testid="owner-op-page">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0A0A0A]/85 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-3">
            <Link to="/pricing" className="hidden sm:inline text-neutral-400 hover:text-white text-sm transition-colors">All plans</Link>
            <Link
              to="/signup"
              onClick={() => trackConversion("signup_click", { variant: "owner_ops", position: "nav" })}
              className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2"
              data-testid="owner-op-nav-signup"
            >
              Start free trial <ArrowRight size={13} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 pt-14 sm:pt-20 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 pill pill-primary mb-5">
              <Truck size={12} weight="fill" /> BUILT FOR 1-3 TRUCK OWNERS
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.02]">
              You are the driver, the <br className="hidden sm:block" />
              dispatcher, and the <br className="hidden sm:block" />
              <span className="text-[#FF5A1F]">accountant.</span>
            </h1>
            <p className="text-neutral-300 text-base sm:text-lg mt-6 leading-relaxed max-w-xl">
              FreightPilot AI™ drafts every dispatch message, invoice, and customer update in 3 seconds.
              So you can drive more, or actually go home when the sun's still up.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/signup"
                onClick={() => trackConversion("signup_click", { variant: "owner_ops", position: "hero" })}
                className="btn-primary rounded-full px-6 py-3.5 text-sm inline-flex items-center justify-center gap-2"
                data-testid="owner-op-hero-signup"
              >
                Start 7-day free trial <ArrowRight size={14} weight="bold" />
              </Link>
              <a
                href="#try-ai"
                className="rounded-full px-6 py-3.5 text-sm border border-white/15 hover:border-white/30 text-white inline-flex items-center justify-center gap-2 transition-colors"
              >
                Try the AI first
              </a>
            </div>
            <div className="mt-6 flex items-center gap-6 text-xs text-neutral-500">
              <div className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-[#FF5A1F]" /> $0 today · card on file
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-[#FF5A1F]" /> Cancel anytime
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-[#FF5A1F]" /> Setup in 60 seconds
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="card-surface p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-[#FF5A1F] text-black text-[9px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-bl-lg">
                LIVE FROM DISPATCH
              </div>
              <div className="overline mb-2">MESSAGE PREVIEW · JAMES → TRUCK T-101</div>
              <div className="rounded-lg bg-[#0F0F0F] border border-white/10 p-4 text-sm text-neutral-100 leading-relaxed">
                James — Pickup confirmed for tomorrow 8am at the Chicago terminal.
                Route to Denver, CO. Watch for freezing rain on I-80 west of Des Moines
                after 6pm. Reply with ETA once loaded. — Mike
              </div>
              <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 text-neutral-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] animate-pulse" />
                  Drafted by AI in 2.8s
                </div>
                <div className="text-[#FF5A1F] font-mono">GPT-5.2</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 py-16 border-t border-white/10">
        <div className="max-w-2xl mb-10">
          <div className="overline mb-3">SOUND FAMILIAR?</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            The problem isn't the trucks. <br />
            <span className="text-[#FF5A1F]">It's everything else.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {PAIN_POINTS.map((p, i) => (
            <div key={i} className="card-surface p-6" data-testid={`pain-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 flex items-center justify-center mb-4">
                <p.icon size={18} className="text-[#FF5A1F]" />
              </div>
              <h3 className="font-display text-lg font-semibold text-white mb-2">{p.title}</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 py-16 border-t border-white/10">
        <div className="max-w-2xl mb-10">
          <div className="overline mb-3">HOW WE HELP</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            Four AI tools that <span className="text-[#FF5A1F]">give you your day back</span>.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          {FEATURES.map((f, i) => (
            <div key={i} className="card-surface p-6 flex gap-4" data-testid={`feature-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 flex items-center justify-center shrink-0">
                <f.icon size={18} className="text-[#FF5A1F]" />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold text-white mb-1">{f.title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Live AI demo */}
      <section id="try-ai" className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 py-16 border-t border-white/10">
        <div className="max-w-2xl mb-8">
          <div className="inline-flex items-center gap-2 pill pill-primary mb-3">
            <Circuitry size={12} weight="fill" /> TRY IT NOW · NO SIGNUP
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.05]">
            Type your scenario. See the AI draft it live.
          </h2>
        </div>
        <div className="card-surface p-6">
          <label className="overline block mb-2">YOUR LOAD SCENARIO</label>
          <textarea
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
            maxLength={500}
            rows={3}
            data-testid="owner-op-scenario-input"
            className="w-full bg-[#141414] border border-white/15 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FF5A1F] resize-none"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-neutral-600 font-mono">{scenario.length}/500</span>
            <button
              onClick={generate}
              disabled={loading}
              data-testid="owner-op-generate"
              className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? "Drafting..." : (<><Sparkle size={13} weight="fill" /> Generate</>)}
            </button>
          </div>
          {output && (
            <div className="mt-5 rounded-lg border border-white/10 bg-[#0F0F0F] p-5" data-testid="owner-op-ai-output">
              <div className="flex items-center justify-between mb-2">
                <div className="overline flex items-center gap-1"><Sparkle size={11} weight="fill" className="text-[#FF5A1F]" /> AI-DRAFTED</div>
                <button onClick={copyOutput} className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white inline-flex items-center gap-1">
                  <Copy size={11} /> Copy
                </button>
              </div>
              <p className="text-sm text-neutral-100 whitespace-pre-wrap leading-relaxed">{output}</p>
              <div className="mt-4 pt-3 border-t border-white/5 text-right">
                <Link
                  to="/signup"
                  onClick={() => trackConversion("signup_click", { variant: "owner_ops", position: "after_ai_demo" })}
                  className="text-xs text-[#FF5A1F] hover:text-[#FF7038] font-medium inline-flex items-center gap-1"
                >
                  Sign up to draft messages like this for your real drivers <ArrowRight size={11} weight="bold" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Pricing (only owner-op tiers) */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 py-16 border-t border-white/10" data-testid="owner-op-pricing">
        <div className="max-w-2xl mb-10">
          <div className="overline mb-3">SIMPLE PRICING</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            Pick your truck count. <br />
            <span className="text-[#FF5A1F]">Start free for 7 days.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {TIERS.map((t, i) => (
            <div
              key={i}
              className={`card-surface p-6 flex flex-col relative overflow-hidden ${t.popular ? "border-[#FF5A1F]/40 ring-1 ring-[#FF5A1F]/20" : ""}`}
              data-testid={`owner-op-tier-${t.name.toLowerCase()}`}
            >
              {t.popular && (
                <div className="absolute top-0 right-0 bg-[#FF5A1F] text-black text-[9px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-bl-lg">
                  MOST POPULAR
                </div>
              )}
              <div className="font-display text-xl font-semibold text-white">{t.name}</div>
              <div className="text-xs text-neutral-500 mt-1 mb-5">{t.tagline}</div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-neutral-500 text-lg">$</span>
                <span className="font-display text-4xl font-semibold text-white">{t.price}</span>
                <span className="text-neutral-500 text-sm">/mo</span>
              </div>
              <div className="text-xs text-neutral-500 mb-5">{t.trucks} · 7-day free trial</div>
              <ul className="space-y-2 flex-1 mb-6">
                {t.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-neutral-300">
                    <CheckCircle size={14} weight="bold" className="text-[#FF5A1F] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/signup"
                onClick={() => trackConversion("signup_click", { variant: "owner_ops", position: `tier_${t.name.toLowerCase()}` })}
                className={`w-full text-center rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                  t.popular ? "bg-[#FF5A1F] text-black hover:bg-[#FF7038]" : "border border-white/15 text-white hover:border-white/30"
                }`}
                data-testid={`owner-op-tier-${t.name.toLowerCase()}-cta`}
              >
                {t.cta} <ArrowRight size={13} weight="bold" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 max-w-3xl mx-auto px-4 sm:px-8 py-16 text-center border-t border-white/10">
        <div className="flex items-center justify-center gap-0.5 mb-4 text-[#FF5A1F]">
          {[0, 1, 2, 3, 4].map(i => <Star key={i} size={14} weight="fill" />)}
        </div>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
          Your first AI-drafted message <br />
          is <span className="text-[#FF5A1F]">60 seconds away</span>.
        </h2>
        <p className="text-neutral-400 mt-5 leading-relaxed">
          7 days free. Cancel anytime.
        </p>
        <Link
          to="/signup"
          onClick={() => trackConversion("signup_click", { variant: "owner_ops", position: "final" })}
          className="btn-primary rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2 mt-8"
          data-testid="owner-op-final-signup"
        >
          Start free trial <ArrowRight size={14} weight="bold" />
        </Link>
      </section>

      <footer className="relative z-10 border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} Freight Pilot AI LLC · FreightPilot AI™</div>
          <div className="flex gap-4">
            <Link to="/pricing" className="hover:text-white">All plans</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <Link to="/roadmap" data-testid="footer-roadmap-link" className="hover:text-white">Roadmap</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
